/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { Fragment } from "react"
import { ListItemText, Divider, ListItem } from "@material-ui/core"
import { CartOrder, CartOrderDetails } from "./cartTypes"
import ListItemActionContainer from "../UI/List/ListItem/ActionContainer"
import { Theme } from "../types"
import { useCartUpdater } from "../../context/CartContext"
import { createPriceValueString } from "../utils/utils"
import DeleteIconButton from "../UI/Button/DeleteIconButton"
import ImageLoader from "../UI/ImageLoader"
import CartListDiscountSign from "./DiscountSign"
import ZebraList from "../UI/List/ZebraList"
import { sumOrderValue } from "../Orders/orderUtils"

const VAT = 0.27

type Props = {
    order: CartOrder<CartOrderDetails>
}

function CartOrderList({ order }: Props) {
    const { cart } = order
    const { removeProductFromCart } = useCartUpdater()

    const handleDelete = (productId: string) => () => {
        removeProductFromCart(productId)
    }

    return (
        <Fragment>
            <ZebraList>
                {Object.keys(cart).map((productId, i) => {
                    return cart[productId].numberInOrder > 0 ? (
                        <ListItem key={productId} data-testid="order_listItem">
                            <div css={styles.imageTitle}>
                                {cart[productId].productInfo?.imagePublicId && (
                                    <ImageLoader
                                        publicId={
                                            cart[productId].productInfo
                                                ?.imagePublicId ?? ""
                                        }
                                        cssStyle={styles.image}
                                        height="100"
                                        width="100"
                                    />
                                )}
                                <ListItemText
                                    primary={cart[productId].productInfo?.name}
                                />
                            </div>
                            <ListItemActionContainer>
                                <CartListDiscountSign
                                    productOrder={cart[productId]}
                                />
                                <span>
                                    {`${cart[productId].numberInOrder} db`}
                                </span>
                                <span
                                    css={styles.priceNumber}
                                    data-testid="order_calculatedPrice"
                                >
                                    {createPriceValueString(
                                        cart[productId].calculatedPrice,
                                        cart[productId].productInfo?.currency ??
                                            ""
                                    )}
                                </span>
                                <span>{" (nettó)"}</span>
                                <p
                                    css={styles.subText}
                                >{`${createPriceValueString(
                                    cart[productId].calculatedPrice * (1 + VAT),
                                    cart[productId].productInfo?.currency ?? ""
                                )} (bruttó)`}</p>
                            </ListItemActionContainer>
                            <DeleteIconButton
                                onRemove={handleDelete(productId)}
                            />
                        </ListItem>
                    ) : null
                })}
            </ZebraList>
            <Divider />
            <div
                css={(theme) => [styles.priceNumber(theme), styles.sum(theme)]}
            >
                {`Összeg:
                    ${createPriceValueString(sumOrderValue(cart), "HUF")}`}
                <p css={styles.subText}>+ Szállítási költséget tartalmazhat</p>
            </div>
        </Fragment>
    )
}

export default CartOrderList

const styles = {
    priceNumber: (theme: Theme) => css`
        font-weight: 600;
        font-size: ${theme.fontSizes.medium};
    `,
    sum: (theme: Theme) => css`
        align-self: flex-end;
        padding: ${theme.spaces.smallScale.s};
    `,
    subText: (theme: Theme) => css`
        color: ${theme.colors.grey[500]};
        margin: 0;
        font-size: ${theme.fontSizes.small};
        font-weight: normal;
    `,
    imageTitle: (theme: Theme) => css`
        flex: 1 1 auto;
        display: flex;
        align-items: flex-start;
        flex-direction: column;
        ${theme.mediaQuery.desktop.default} {
            align-items: center;
            flex-direction: row;
        }
    `,
    image: (theme: Theme) => css`
        padding: ${theme.spaces.smallScale.xs} 0 0 0;
        order: 1;
        ${theme.mediaQuery.desktop.default} {
            padding-right: ${theme.spaces.smallScale.s};
            order: 0;
        }
    `,
}
